import { DatePipe } from '@angular/common';
import {
    AfterViewInit,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation
} from '@angular/core';
import clone from 'just-clone';
import moment from 'moment';
import * as XLSX from 'xlsx';
import * as fromLookups from '../../lookups';
import { Process, ResponseStatus } from '../../shared';
import { MenuItem, TreeNode } from '../../shared/efaact-primeng.module';
import { IShowErrors } from '../../shared/errors-sidenav';
import { UnsavedChangesService } from '../../shared/modal-dialog';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { IIndirectStructure } from '../IIndirectStructure';
import { Account } from '../../account';
import { ReplaySubject } from 'rxjs';
import { PayrollCode } from '../../payroll/third-party';

@Component({
    selector: 'ef-indirect-jobs-presenters',
    templateUrl: './indirect-jobs-presenters.component.html',
    styleUrls: ['./indirect-jobs-presenters.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class IndirectJobsPresentersComponent
    implements OnInit, OnDestroy, AfterViewInit
{
    public errorHelpText = '';
    public active = true;
    public loadingData = true;
    public flattenedJobs: Array<IIndirectStructure> = [];
    public downloadOptions: Array<MenuItem> = [];
    public isVisible = true;
    public treeNodes: TreeNode<IIndirectStructure>[] = [];
    private expandedNodeKeys = new Set<string>();

    @Input() public process: Process;
    @Input() public indirectTypes: Array<fromLookups.IndirectType>;
    private _userGroups: Array<fromLookups.IUserGroup> = [];
    @Input() public set userGroups(value: Array<fromLookups.IUserGroup>) {
        const groups = clone(value ?? []);
        // Preserve old behavior: only "C*" groups participate in Cost Center override.
        const costCenterGroups = groups.filter(x =>
            (x.PrefixedGroupID ?? '').includes('C')
        );
        costCenterGroups.push({
            PrefixedGroupID: '',
            GroupID: null,
            GroupName: '',
            GroupPurpose: ''
        });
        this._userGroups = costCenterGroups;
        this.isVisible = this._userGroups.length > 2;
    }
    public get userGroups(): Array<fromLookups.IUserGroup> {
        return this._userGroups;
    }

    @Output() public ShowError = new EventEmitter<IShowErrors>();
    @Output() rowUpdate = new EventEmitter<Array<IIndirectStructure>>();
    @Output() resetData = new EventEmitter();
    @Output() resetFilters = new EventEmitter();
    @Output() GetAccounts = new ReplaySubject<any>(1);

    private _jobs: Array<IIndirectStructure> = [];
    @Input('jobs') set jobs(value: Array<IIndirectStructure>) {
        const incoming = clone(value ?? []);
        this.captureExpandedKeysFromNodes(this.treeNodes);
        if (this.treeNodes.length === 0 && this.expandedNodeKeys.size === 0) {
            this.collectAllKeys(incoming).forEach(key => this.expandedNodeKeys.add(key));
        }
        this._jobs = incoming;
        this.flattenedJobs = this.flattenNestedData(incoming);
        this.treeNodes = this.mapJobsToTreeNodes(incoming);
        this.loadingData = false;
    }
    get jobs(): Array<IIndirectStructure> {
        return this._jobs;
    }

    private _accounts: Array<Account> = [];
    @Input('accounts') set accounts(value: Array<Account>) {
        this._accounts = clone(value ?? []);
    }
    get accounts(): Array<Account> {
        return this._accounts;
    }

    private _paycodes: Array<PayrollCode> = [];
    @Input('paycodes') set paycodes(value: Array<PayrollCode>) {
        this._paycodes = clone(value ?? []);
    }
    get paycodes(): Array<PayrollCode> {
        return this._paycodes;
    }

    private _errors: Array<string>;
    @Input() public set errors(value: Array<string>) {
        this.log.debug({ type: 'IndirectJobsPresenterComponent:errors:set', value });
        this._errors = value;
        this.errorHelpText = value?.length
            ? value.reduce((a, c) => a + c + '\r\n', '').trim()
            : '';
    }
    public get errors(): Array<string> {
        return this._errors;
    }

    private _selectedError: IShowErrors;
    @Input() public set selectedError(value: IShowErrors) {
        this.log.debug({
            type: 'IndirectJobsPresenterComponent:selectedError:set',
            value
        });
        if (this.process && value?.MsgProcessID === this.process.ProcessID) {
            this._selectedError = value;
        } else {
            return;
        }
        if (value?.MsgEntityID) {
            this.expandAncestorsFor(String(value.MsgEntityID));
        }
    }
    public get selectedError(): IShowErrors {
        return this._selectedError;
    }

    constructor(
        @Inject(LoggingService) private log: LoggingService,
        @Inject(UnsavedChangesService)
        private confirmservice: UnsavedChangesService,
        private datePipe: DatePipe
    ) {}

    ngOnDestroy() {
        this.active = false;
        this.resetData.emit();
    }

    ngOnInit() {
        this.downloadOptions = [
            { label: 'CSV', icon: 'pi pi-download', command: () => this.downloadTable('csv') },
            { label: 'Excel', icon: 'pi pi-download', command: () => this.downloadTable('xlsx') }
        ];
    }

    ngAfterViewInit() {}

    public onTreeNodeExpand(event: any): void {
        const key = this.extractNodeKey(event?.node);
        if (key) this.expandedNodeKeys.add(key);
    }

    public onTreeNodeCollapse(event: any): void {
        const key = this.extractNodeKey(event?.node);
        if (key) this.expandedNodeKeys.delete(key);
    }

    public downloadTable(type: string) {
        const exportRows = this.flattenedJobs.map(row => ({
            'Job Name': row.QB_Name,
            'Labor Type': this.indirectTypeLabel(row.IndirectType),
            Account: this.accountLabel(row.AccountListID),
            Paycode: this.paycodeLabel(row.Paycodeid),
            'Accrue Leave': row.AccruedLeave ? 'YES' : '',
            Sick: row.IsSickTask ? 'YES' : '',
            Holiday: row.IsHolidayTask ? 'YES' : '',
            'Leave Request': row.IsLeaveTask ? 'YES' : '',
            LWOP: row.LWOPIndicator ? 'YES' : '',
            'Leave Payout': row.LeavePayout ? 'YES' : '',
            'Cost Center Override': this.costCenterLabel(row.CostCenterID),
            'First Process Date': this.formatFirstProcessDate(row.FirstProcessDate)
        }));
        const worksheet = XLSX.utils.json_to_sheet(exportRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Indirect Job Structure');
        XLSX.writeFile(
            workbook,
            `${this.process.ProcessTitle}_${moment(new Date()).format('MM-DD-YYYY')}.${type}`
        );
    }

    public clearSelectedRows() {}

    public expandCollapse(action: string) {
        this.setExpandedState(this.treeNodes, action === 'expand');
        this.treeNodes = [...this.treeNodes];
    }

    public clearFilters() {}

    public mapJobsToTreeNodes(
        jobs: Array<IIndirectStructure>
    ): TreeNode<IIndirectStructure>[] {
        return (jobs ?? []).map(job => ({
            key: job.QB_ListID,
            data: job,
            expanded: this.expandedNodeKeys.has(job.QB_ListID),
            children: this.mapJobsToTreeNodes(job._children ?? [])
        }));
    }

    public statusClass(row: IIndirectStructure): string {
        const code = row?.Status?.StatusCode;
        if (code === 1) return 'green';
        if (code === 2) return 'yellow';
        if (code && code >= 3 && code <= 6) return 'red';
        return '';
    }

    public onStatusClick(row: IIndirectStructure): void {
        const status = row?.Status;
        if (status && [2, 3, 4, 5].includes(status.StatusCode)) {
            this.ShowError.emit({ open: true, MsgEntityID: status.MsgEntityID });
        }
    }

    public canEditChargeFlag(row: IIndirectStructure): boolean {
        return !!row?.QB_IsActive && !!row?.IsChargeNumber && row?.IndirectType === 'FRL' && !row?.IsContract;
    }

    public canEditLaborType(row: IIndirectStructure): boolean {
        return !!row?.QB_IsActive && !!row?.IsChargeNumber && !row?.IsContract;
    }

    public canEditAccountPaycode(row: IIndirectStructure): boolean {
        return !!(row?.IsChargeNumber || row?.IsContract);
    }

    public canEditCostCenter(row: IIndirectStructure): boolean {
        if (!row?.QB_IsActive || !this.isVisible) return false;
        if (row?.IsContract || !row?.IsChargeNumber) return false;
        return row?.IndirectType === 'OHL' || row?.IndirectType === 'FRL';
    }

    public toggleAccruedLeave(row: IIndirectStructure): void {
        if (!this.canEditChargeFlag(row)) return;
        const apply = () => {
            row.AccruedLeave = !row.AccruedLeave;
            row.Status = this.loadingStatus();
            this.updateRow(row);
        };
        if (row.FirstProcessDate) {
            this.confirmservice
                .confirmPromise({
                    dialogText:
                        'Are you sure? Changing an accrue leave job requires synchronizing the fiscal year, this can take a few minutes.',
                    okText: 'Yes',
                    cancelText: 'No'
                })
                .then(apply)
                .catch(() => {});
            return;
        }
        apply();
    }

    public toggleSick(row: IIndirectStructure): void {
        if (!this.canEditChargeFlag(row)) return;
        row.IsSickTask = !row.IsSickTask;
        row.Status = this.loadingStatus();
        this.updateRow(row);
    }

    public toggleHoliday(row: IIndirectStructure): void {
        if (!this.canEditChargeFlag(row)) return;
        row.IsHolidayTask = !row.IsHolidayTask;
        row.Status = this.loadingStatus();
        this.updateRow(row);
    }

    public toggleLeaveRequest(row: IIndirectStructure): void {
        if (!this.canEditChargeFlag(row)) return;
        row.IsLeaveTask = !row.IsLeaveTask;
        row.Status = this.loadingStatus();
        this.updateRow(row);
    }

    public toggleLWOP(row: IIndirectStructure): void {
        if (!this.canEditChargeFlag(row)) return;
        row.LWOPIndicator = !row.LWOPIndicator;
        row.Status = this.loadingStatus();
        this.updateRow(row);
    }

    public toggleLeavePayout(row: IIndirectStructure): void {
        if (!this.canEditChargeFlag(row)) return;
        row.LeavePayout = !row.LeavePayout;
        row.Status = this.loadingStatus();
        this.updateRow(row);
    }

    public changeLaborType(row: IIndirectStructure, value: string): void {
        if (!value || row.IndirectType === value || !this.canEditLaborType(row)) return;
        const apply = () => {
            row.IndirectType = value;
            row.Status = this.loadingStatus();
            this.updateRow(row);
        };
        if (row.FirstProcessDate) {
            this.confirmservice
                .confirmPromise({
                    dialogText:
                        'Are you sure? Changing labor type requires synchronizing the fiscal year, this can take a few minutes.',
                    okText: 'Yes',
                    cancelText: 'No'
                })
                .then(apply)
                .catch(() => {});
            return;
        }
        apply();
    }

    public changeAccount(row: IIndirectStructure, value: string): void {
        if (row.AccountListID === value || !this.canEditAccountPaycode(row)) return;
        row.AccountListID = value;
        row.Status = this.loadingStatus();
        this.updateRow(row);
    }

    public changePaycode(row: IIndirectStructure, value: number): void {
        if (row.Paycodeid === value || !this.canEditAccountPaycode(row)) return;
        row.Paycodeid = value;
        row.Status = this.loadingStatus();
        this.updateRow(row);
    }

    public changeCostCenter(row: IIndirectStructure, value: number): void {
        if (row.CostCenterID === value || !this.canEditCostCenter(row)) return;
        const prev = row.CostCenterID;
        const apply = () => {
            row.CostCenterID = value;
            row.Status = this.loadingStatus();
            this.updateRow(row);
        };
        if (row.FirstProcessDate) {
            this.confirmservice
                .confirmPromise({
                    dialogText: 'Are you sure you want to change the Cost Center Override?',
                    okText: 'Yes',
                    cancelText: 'No'
                })
                .then(apply)
                .catch(() => {
                    row.CostCenterID = prev;
                });
            return;
        }
        apply();
    }

    public indirectTypeOptions() {
        return (this.indirectTypes ?? [])
            .map(x => ({ label: x.CalcDesc, value: x.CalcType }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    public accountOptions() {
        return (this.accounts ?? [])
            .map(x => ({ label: x.AccountCode, value: x.AccountListID }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    public paycodeOptions() {
        return (this.paycodes ?? [])
            .map(x => ({ label: x.Paycode, value: x.Paycodeid }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    public costCenterOptions() {
        return (this.userGroups ?? [])
            .filter(x => x.PrefixedGroupID?.includes('C') || x.GroupID == null)
            .map(x => ({ label: x.GroupName, value: x.GroupID }))
            .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));
    }

    public indirectTypeLabel(value: string): string {
        return this.indirectTypes?.find(x => x.CalcType === value)?.CalcDesc ?? value;
    }

    public accountLabel(value: string): string {
        return this.accounts?.find(x => x.AccountListID === value)?.AccountCode ?? (value ?? '');
    }

    public paycodeLabel(value: number): string {
        if (!value) return '';
        return this.paycodes?.find(x => x.Paycodeid === value)?.Paycode ?? '';
    }

    public costCenterLabel(value: number): string {
        return this.userGroups?.find(x => x.GroupID === +value)?.GroupName ?? String(value ?? '');
    }

    public formatFirstProcessDate(value: string): string {
        if (!value) return '';
        const date = new Date(this.datePipe.transform(value, 'MM/dd/yyyy'));
        return date.toDateString();
    }

    public flattenNestedData(
        cs: Array<IIndirectStructure>
    ): Array<IIndirectStructure> {
        const flatten = ary =>
            ary.reduce((r, { _children, ...rest }) => {
                r.push(rest);
                if (_children) {
                    r.push(...flatten(_children));
                }
                return r;
            }, []);
        return flatten(cs ?? []);
    }

    public isSelectedErrorRow(row: IIndirectStructure): boolean {
        return String(this.selectedError?.MsgEntityID ?? '') === row?.QB_ListID;
    }

    public updateRow(rowData: IIndirectStructure) {
        const rowDataForUpdate: IIndirectStructure = {
            Paycodeid: rowData.Paycodeid,
            AccountListID: rowData.AccountListID,
            QB_SubLevel: rowData.QB_SubLevel,
            QB_ListID: rowData.QB_ListID,
            QB_IsActive: rowData.QB_IsActive,
            QB_FullName: rowData.QB_FullName,
            QB_Name: rowData.QB_Name,
            QB_ParentListID: rowData.QB_ParentListID,
            QB_QBParentName: rowData.QB_QBParentName,
            IsContract: rowData.IsContract,
            IsChargeNumber: rowData.IsChargeNumber,
            ContractType: rowData.ContractType,
            IndirectType: rowData.IndirectType,
            IsHolidayTask: rowData.IsHolidayTask,
            IsSickTask: rowData.IsSickTask,
            IsLeaveTask: rowData.IsLeaveTask,
            AccruedLeave: rowData.AccruedLeave,
            LWOPIndicator: rowData.LWOPIndicator,
            CostCenterID: rowData.CostCenterID,
            FirstProcessDate: rowData.FirstProcessDate,
            LeavePayout: rowData.LeavePayout,
            Status: {
                StatusCode: null,
                Message: null,
                MsgProcessID: null,
                MsgEntityID: null
            },
            _children: []
        };
        this.rowUpdate.emit([rowDataForUpdate]);
    }

    private loadingStatus() {
        return { StatusCode: ResponseStatus.Loading, Message: '' };
    }

    private extractNodeKey(node: any): string | null {
        return (node?.key ?? node?.data?.QB_ListID ?? null) as string | null;
    }

    private captureExpandedKeysFromNodes(nodes: TreeNode<IIndirectStructure>[]): void {
        for (const node of nodes ?? []) {
            const key = this.extractNodeKey(node);
            if (key && node.expanded) this.expandedNodeKeys.add(key);
            if (node.children?.length) this.captureExpandedKeysFromNodes(node.children);
        }
    }

    private setExpandedState(
        nodes: TreeNode<IIndirectStructure>[],
        expanded: boolean
    ): void {
        for (const node of nodes ?? []) {
            node.expanded = expanded;
            const key = this.extractNodeKey(node);
            if (key) {
                if (expanded) this.expandedNodeKeys.add(key);
                else this.expandedNodeKeys.delete(key);
            }
            if (node.children?.length) this.setExpandedState(node.children, expanded);
        }
    }

    private setNodeExpanded(
        nodes: TreeNode<IIndirectStructure>[],
        key: string,
        expanded: boolean
    ): boolean {
        for (const node of nodes ?? []) {
            if (node.key === key) {
                node.expanded = expanded;
                if (expanded) this.expandedNodeKeys.add(key);
                else this.expandedNodeKeys.delete(key);
                return true;
            }
            if (node.children?.length && this.setNodeExpanded(node.children, key, expanded)) {
                return true;
            }
        }
        return false;
    }

    private expandAncestorsFor(listId: string): void {
        let current = this.flattenedJobs.find(x => x.QB_ListID === listId);
        while (current?.QB_ParentListID) {
            this.setNodeExpanded(this.treeNodes, current.QB_ParentListID, true);
            current = this.flattenedJobs.find(x => x.QB_ListID === current.QB_ParentListID);
        }
        this.treeNodes = [...this.treeNodes];
    }

    private collectAllKeys(items: IIndirectStructure[]): string[] {
        const keys: string[] = [];
        const walk = (nodes: IIndirectStructure[]) => {
            for (const node of nodes ?? []) {
                keys.push(node.QB_ListID);
                if (node._children?.length) walk(node._children);
            }
        };
        walk(items ?? []);
        return keys;
    }
}
